import { Box, SxProps, Typography } from "@outschool/backpack";
import { Activity, PublicReview } from "@outschool/gql-backend-generated";
import { PublicReviewFragmentFragment } from "@outschool/gql-frontend-generated";
import { useMachineTranslation, useTranslation } from "@outschool/localization";
import { createPathWithActivity, parentPath } from "@outschool/routes";
import { formatDate } from "@outschool/time";
import { useSession } from "@outschool/ui-auth";
import {
  AttachmentGallery,
  UserHeadshotImage,
} from "@outschool/ui-components-shared";
import {
  TrackingProperties,
  useImpressionTracking,
} from "@outschool/ui-legacy-component-library";
import { Screen, useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import CollapsedContent from "../CollapsedContent";
import { MoreOptionsButton } from "../MoreOptionsButton";
import StarRatings from "../StarRatings";
import { ReviewCaption } from "./ReviewCaption";
import { TeacherResponse } from "./TeacherResponse";

type PublicReviewQuoteProps = {
  // @ts-ignore TS(2339): Property 'leader' does not exist on type '{ __type... Remove this comment to see the full error message
  teacher?: PublicReviewFragmentFragment["activity"]["leader"];
  activity: PublicReviewFragmentFragment["activity"];
  review: PublicReviewFragmentFragment;
  trackingLabel?: string;
  containerSx?: SxProps;
  reviewSx?: SxProps;
  shouldShowMoreOptions?: boolean;
  shouldShowClassLinkBelowReviewdAt?: boolean;
  showMoreMaxChars?: number;
  ReviewRespondButton?: React.ElementType;
  shouldShowClassLinkBelowStarRating?: boolean;
};

export const PublicReviewQuote: React.FunctionComponent<
  PublicReviewQuoteProps
> = ({
  teacher,
  activity,
  review,
  trackingLabel = "public_review_quote",
  containerSx,
  reviewSx,
  shouldShowMoreOptions = true,
  shouldShowClassLinkBelowReviewdAt = true,
  showMoreMaxChars = 550,
  ReviewRespondButton,
  shouldShowClassLinkBelowStarRating = false,
}) => {
  const { t } = useTranslation(
    "ui-components-website\\src\\Reviews\\PublicReviewQuote\\PublicReviewQuote"
  );
  const { translate } = useMachineTranslation();

  const isMobile = Screen.useIsMobile();
  const { userTimeZone, currentUser } = useSession();
  const Link = useLinkComponent();

  // @ts-ignore TS(2345): Argument of type 'string | null | undefined' is no... Remove this comment to see the full error message
  const publicReview = translate(review.details?.publicReview);
  const name = review.parent?.publicName ?? t`a parent`;
  const parentUid = review.parent?.uid;
  const reviewUid = review?.uid;
  const activityUid = activity?.uid;
  const teacherUid = teacher?.uid;

  const isCurrentUserTeacher =
    currentUser && teacher && currentUser.uid === teacher.uid;

  const hasAttachments = review.attachments?.length > 0 || false;

  const maxCharacters = isMobile ? showMoreMaxChars / 2 : showMoreMaxChars;

  const reviewedAt = React.useMemo(
    // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
    () => formatDate(review.created_at, userTimeZone, false),
    [review.created_at, userTimeZone]
  );

  const activityRoute = React.useMemo(
    // @ts-ignore TS(2345): Argument of type '{ __typename?: "Activity" | unde... Remove this comment to see the full error message
    () => createPathWithActivity(activity),
    [activity]
  );

  const parentProfileRoute = React.useMemo(
    () => parentPath(parentUid),
    [parentUid]
  );

  /** impression tracking */
  const [publicReviewNode, setPublicReviewNode] =
    React.useState<HTMLDivElement>();
  const trackingMetadata: TrackingProperties = React.useMemo(
    () => ({
      // for backwards compatability
      teacherUid,
      reviewUid,
      activityUid,
      // current tracking meta
      activity_uid: activityUid,
      teacher_uid: teacherUid,
      feedback_uid: reviewUid,
      feedback_author_uid: parentUid,
    }),
    [activityUid, reviewUid, teacherUid, parentUid]
  );
  useImpressionTracking({
    node: publicReviewNode,
    trackingLabel,
    uniqueId: reviewUid,
    sharedProperties: trackingMetadata,
  });

  return (
    <Box
      sx={[
        {
          textAlign: "left",
          paddingTop: "12px",
          paddingBottom: "1em",
        },
        ...(Array.isArray(containerSx) ? containerSx : [containerSx]),
      ]}
      ref={setPublicReviewNode}
    >
      <Box
        sx={[
          {
            borderTopRightRadius: "24px",
            borderTopLeftRadius: "24px",
            borderBottomRightRadius: "24px",
            borderBottomLeftRadius: "8px",
            backgroundColor: "#F6F6F8",
            paddingX: "19px",
            paddingTop: "19px",
            paddingBottom: "10px",
            position: "relative",
            display: "block",
            gridTemplateRows: "25px 30px 2fr",
          },
          ...(Array.isArray(reviewSx) ? reviewSx : [reviewSx]),
        ]}
      >
        <Box
          flex
          sx={{
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          {review.activity_star_rating && (
            <StarRatings
              rating={review.activity_star_rating}
              sx={{ marginBottom: "0.5em" }}
            />
          )}

          <Typography
            variant="inherit"
            sx={{
              fontSize: "14px",
              color: "grey.500",
            }}
          >
            {review.currentUserCanEdit && shouldShowMoreOptions && (
              <MoreOptionsButton
                activity={activity as Activity}
                review={review as PublicReview}
              />
            )}
          </Typography>
        </Box>

        {shouldShowClassLinkBelowStarRating && (
          <Box
            sx={{
              paddingTop: "0.5em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: "0px",
            }}
          >
            <Link
              to={activityRoute}
              trackingName="teacher_class_review"
              trackingUniqueId={activityUid}
            >
              <Typography
                variant="inherit"
                sx={{
                  color: "primary.800",
                  fontWeight: "fontWeightMedium",
                }}
              >
                {/* Todo - change to titleTranslated, add to GQL query */}
                {activity?.title}
              </Typography>
            </Link>
          </Box>
        )}

        <Box
          sx={{
            paddingBottom: "0.5em",
          }}
        >
          <CollapsedContent
            maxCharacters={maxCharacters}
            sx={{
              whiteSpace: "pre-wrap",
              WebkitLineClamp: 4,
            }}
            buttonSx={{
              color: "primary.800",
              verticalAlign: "inherit",
            }}
            // place teacher response behind show/hide
            additionalContent={
              teacher &&
              review.teacher_response && (
                <TeacherResponse teacher={teacher} review={review} />
              )
            }
          >
            {publicReview}
          </CollapsedContent>

          {hasAttachments && (
            <AttachmentGallery
              attachments={review.attachments}
              trackingLabel={trackingLabel}
              trackingMetadata={trackingMetadata}
              captionForAttachment={() => <ReviewCaption review={review} />}
              numPrimaryAttachments={5}
              containerSx={{ marginTop: "small" }}
              /* eslint-disable i18next/no-literal-string */
              primaryAttachmentMaxHeight="10vh"
              secondaryAttachmentMaxHeight="8vh"
              /* eslint-enable i18next/no-literal-string */
            />
          )}
        </Box>

        {isCurrentUserTeacher && !!ReviewRespondButton && (
          <ReviewRespondButton activity={activity} review={review} />
        )}

        <Box
          sx={{
            paddingRight: "1em",
          }}
        >
          <Box
            flex
            sx={{
              alignItems: "center",
            }}
          >
            <UserHeadshotImage
              user={review.parent}
              size={44}
              sx={{ marginRight: "medium" }}
            />

            <Box
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "flex",
                fontSize: "1.6rem",
                lineHeight: "2.4rem",
                flexWrap: "wrap",
                rowGap: 0,
                columnGap: "0.5rem",
              }}
            >
              <Link
                to={parentProfileRoute}
                target={isMobile ? undefined : "_blank"}
              >
                <Typography
                  variant="inherit"
                  sx={{
                    color: "primary.700",
                    display: "block",
                  }}
                >
                  {name}
                </Typography>
              </Link>
              <Typography
                variant="caption"
                sx={{
                  color: "grey.500",
                  fontSize: "inherit",
                  display: "contents",
                }}
              >
                {t("on {{reviewedAt}}", { reviewedAt })}
              </Typography>
              {shouldShowClassLinkBelowReviewdAt && (
                <Link
                  to={activityRoute}
                  trackingName="teacher_class_review"
                  trackingUniqueId={activityUid}
                >
                  <Typography
                    variant="inherit"
                    sx={{
                      color: "primary.700",
                      fontWeight: "fontWeightMedium",
                    }}
                  >
                    {/* Todo - change to titleTranslated, add to GQL query */}
                    {activity?.title}
                  </Typography>
                </Link>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
